<template>
	<section class="service-tokens">
		<div class="header">
			<div>
				<h2>{{ t("adminPanelPage.serviceTokens.title") }}</h2>
				<p>{{ t("adminPanelPage.serviceTokens.description") }}</p>
			</div>
			<Button variant="primary" size="m" :disabled="loading" @click="showCreate = true">
				{{ t("adminPanelPage.serviceTokens.create") }}
			</Button>
		</div>

		<div v-if="createdToken" class="token-reveal">
			<strong>{{ t("adminPanelPage.serviceTokens.copyWarning") }}</strong>
			<div class="token-value">{{ createdToken }}</div>
			<Button variant="secondary" size="s" @click="copyToken">{{ t("adminPanelPage.serviceTokens.copy") }}</Button>
			<Button variant="ghost" size="s" @click="createdToken = ''">{{ t("adminPanelPage.serviceTokens.close") }}</Button>
		</div>

		<div v-if="error" class="error">{{ error }}</div>
		<div v-if="loading" class="loading">{{ t("adminPanelPage.serviceTokens.loading") }}</div>
		<div v-else-if="tokens.length === 0" class="empty">{{ t("adminPanelPage.serviceTokens.empty") }}</div>
		<div v-else class="token-table">
			<div class="row header-row">
				<span>{{ t("adminPanelPage.serviceTokens.name") }}</span>
				<span>{{ t("adminPanelPage.serviceTokens.permissions") }}</span>
				<span>{{ t("adminPanelPage.serviceTokens.createdBy") }}</span>
				<span>{{ t("adminPanelPage.serviceTokens.expires") }}</span>
				<span>{{ t("adminPanelPage.serviceTokens.status") }}</span
				><span>{{ t("adminPanelPage.serviceTokens.revokedBy") }}</span
				><span></span>
			</div>
			<div v-for="token in tokens" :key="token.id" class="row">
				<span>{{ token.name }}</span>
				<span>{{ token.permissions.join(", ") }}</span>
				<span>{{ actorLabel(token.createdByUser, token.createdBy) }}</span>
				<span>{{ formatDate(token.expiresAt) }}</span>
				<span>{{
					token.revokedAt ? t("adminPanelPage.serviceTokens.revoked") : t("adminPanelPage.serviceTokens.active")
				}}</span>
				<span>{{ token.revokedByUser ? actorLabel(token.revokedByUser) : "-" }}</span>
				<Button
					variant="danger"
					size="s"
					:disabled="Boolean(token.revokedAt) || revoking === token.id"
					@click="revoke(token)"
				>
					{{ t("adminPanelPage.serviceTokens.revoke") }}
				</Button>
			</div>
		</div>

		<div v-if="showCreate" class="modal-overlay" @click.self="showCreate = false">
			<form class="modal" @submit.prevent="create">
				<h3>{{ t("adminPanelPage.serviceTokens.createTitle") }}</h3>
				<label class="field">
					<span class="field-label">{{ t("adminPanelPage.serviceTokens.name") }}</span>
					<input
						v-model="name"
						required
						type="text"
						class="field-input"
						:disabled="saving"
						:placeholder="t('adminPanelPage.serviceTokens.namePlaceholder')"
						autocomplete="off"
					/>
				</label>
				<div class="field">
					<span class="field-label">{{ t("adminPanelPage.serviceTokens.permissions") }}</span>
					<div v-if="selectedPermissions.length > 0" class="selected-permissions">
						<span v-for="permission in selectedPermissions" :key="permission" class="permission-chip">
							<span class="permission-name">{{ permission }}</span>
							<button
								type="button"
								class="permission-remove"
								:disabled="saving"
								:aria-label="t('adminPanelPage.serviceTokens.removePermission', { permission })"
								@click="removePermission(permission)"
							>
								x
							</button>
						</span>
					</div>
					<ArkListboxDropdown
						:model-value="selectedPermissions"
						@update:model-value="handlePermissionChange"
						:options="permissionOptions"
						:multiple="true"
						:searchable="true"
						:clearable="false"
						:disabled="saving"
						:placeholder="t('adminPanelPage.serviceTokens.permissionsPlaceholder')"
						:search-placeholder="t('adminPanelPage.serviceTokens.permissionsSearchPlaceholder')"
						:no-options-text="t('adminPanelPage.serviceTokens.permissionsNoOptions')"
					>
						<template #trigger="{ isOpen }">
							<button type="button" class="permissions-trigger" :disabled="saving">
								<span>{{ t("adminPanelPage.serviceTokens.permissionsPlaceholder") }}</span>
								<span class="chevron" :class="{ open: isOpen }"></span>
							</button>
						</template>
					</ArkListboxDropdown>
				</div>
				<label class="field">
					<span class="field-label">{{ t("adminPanelPage.serviceTokens.duration") }}</span>
					<input
						v-model="expiresIn"
						required
						type="text"
						class="field-input"
						:disabled="saving"
						:placeholder="t('adminPanelPage.serviceTokens.durationPlaceholder')"
						autocomplete="off"
					/>
				</label>
				<div v-if="formError" class="error">{{ formError }}</div>
				<div class="actions">
					<Button type="button" variant="secondary" size="m" :disabled="saving" @click="showCreate = false">{{
						t("adminPanelPage.serviceTokens.cancel")
					}}</Button>
					<Button type="submit" variant="primary" size="m" :disabled="saving || selectedPermissions.length === 0">{{
						saving ? t("adminPanelPage.serviceTokens.creating") : t("adminPanelPage.serviceTokens.create")
					}}</Button>
				</div>
			</form>
		</div>
	</section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import Button from "@/components/ui/Button.vue"
import ArkListboxDropdown from "@/components/ui/ArkListboxDropdown.vue"
import {
	createServiceToken,
	getAllPermissions,
	getServiceTokens,
	revokeServiceToken,
	type ServiceToken,
	type RolePermission,
} from "@/utils/ApiClient"

const props = defineProps<{ apiToken: string }>()
const { t } = useI18n()
const tokens = ref<ServiceToken[]>([])
const availablePermissions = ref<RolePermission[]>([])
const loading = ref(true)
const saving = ref(false)
const revoking = ref<string | null>(null)
const error = ref("")
const formError = ref("")
const showCreate = ref(false)
const createdToken = ref("")
const name = ref("")
const selectedPermissions = ref<string[]>([])
const expiresIn = ref("365d")

const permissionOptions = computed(() =>
	availablePermissions.value.map(permission => ({ value: permission.name, label: permission.name }))
)

const formatDate = (value: string) => new Date(value).toLocaleDateString()

const actorLabel = (actor: { name: string | null; email: string } | null | undefined, fallbackId?: string) =>
	actor?.name || actor?.email || fallbackId || "-"

const load = async () => {
	loading.value = true
	try {
		const [serviceTokens, permissions] = await Promise.all([
			getServiceTokens(props.apiToken),
			getAllPermissions(props.apiToken),
		])
		tokens.value = serviceTokens
		availablePermissions.value = permissions
	} catch (err) {
		error.value = err instanceof Error ? err.message : t("adminPanelPage.serviceTokens.loadError")
	} finally {
		loading.value = false
	}
}

const create = async () => {
	saving.value = true
	formError.value = ""
	try {
		const result = await createServiceToken(props.apiToken, {
			name: name.value,
			permissions: selectedPermissions.value,
			expiresIn: expiresIn.value,
		})
		createdToken.value = result.token
		showCreate.value = false
		name.value = ""
		selectedPermissions.value = []
		await load()
	} catch (err) {
		formError.value = err instanceof Error ? err.message : t("adminPanelPage.serviceTokens.createError")
	} finally {
		saving.value = false
	}
}

const revoke = async (token: ServiceToken) => {
	if (!window.confirm(t("adminPanelPage.serviceTokens.confirmRevoke", { name: token.name }))) return
	revoking.value = token.id
	try {
		await revokeServiceToken(props.apiToken, token.id)
		await load()
	} catch (err) {
		error.value = err instanceof Error ? err.message : t("adminPanelPage.serviceTokens.revokeError")
	} finally {
		revoking.value = null
	}
}

const copyToken = async () => {
	await navigator.clipboard.writeText(createdToken.value)
}

const handlePermissionChange = (value: string | number | null | (string | number | null)[]) => {
	selectedPermissions.value = (Array.isArray(value) ? value : [value])
		.filter((permission): permission is string | number => permission != null)
		.map(String)
}

const removePermission = (permissionToRemove: string) => {
	selectedPermissions.value = selectedPermissions.value.filter(permission => permission !== permissionToRemove)
}

onMounted(load)
</script>

<style scoped>
.service-tokens {
	padding: var(--space-20);
	border-top: var(--border-width-thin) solid var(--neutral-200);
}
.header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: var(--space-16);
	margin-bottom: var(--space-20);
}
h2,
h3,
p {
	margin: 0;
}
p {
	color: var(--neutral-600);
	margin-top: var(--space-4);
}
.token-table {
	border: var(--border-width-thin) solid var(--neutral-200);
	overflow-x: auto;
}
.row {
	display: grid;
	grid-template-columns: minmax(140px, 1fr) minmax(220px, 2fr) minmax(150px, 1fr) 120px 90px minmax(150px, 1fr) 90px;
	gap: var(--space-12);
	padding: var(--space-12);
	align-items: center;
	border-bottom: var(--border-width-thin) solid var(--neutral-200);
}
.row:last-child {
	border-bottom: 0;
}
.header-row {
	font-weight: var(--font-weight-semibold);
	background: var(--neutral-50);
}
.token-reveal {
	display: flex;
	flex-wrap: wrap;
	gap: var(--space-10);
	padding: var(--space-16);
	margin-bottom: var(--space-16);
	background: var(--neutral-50);
}
.token-value {
	flex-basis: 100%;
	overflow-wrap: anywhere;
	font-family: monospace;
}
.error {
	color: var(--accent-warning);
	padding: var(--space-10) 0;
}
.empty,
.loading {
	color: var(--neutral-600);
}
.modal-overlay {
	position: fixed;
	inset: 0;
	display: grid;
	place-items: center;
	padding: var(--space-20);
	overflow-y: auto;
	background: rgb(0 0 0 / 50%);
	z-index: 1000;
}
.modal {
	width: min(90%, 520px);
	max-height: calc(100dvh - var(--space-40));
	overflow-y: auto;
	display: grid;
	gap: var(--space-16);
	padding: var(--space-20);
	background: var(--neutral-0);
	border-radius: var(--border-radius-md);
	box-shadow: var(--shadow-md);
}
.field {
	display: grid;
	gap: var(--space-4);
}

.field-label {
	font-weight: var(--font-weight-medium);
}

.selected-permissions {
	display: flex;
	flex-wrap: wrap;
	gap: var(--space-6);
}

.permission-chip {
	display: inline-flex;
	align-items: center;
	max-width: 100%;
	gap: var(--space-6);
	padding: var(--space-4) var(--space-6) var(--space-4) var(--space-8);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-base);
	background: var(--neutral-50);
	color: var(--neutral-900);
	font-size: var(--font-size-small);
}

.permission-name {
	overflow-wrap: anywhere;
}

.permission-remove {
	display: inline-flex;
	flex: 0 0 auto;
	align-items: center;
	justify-content: center;
	width: var(--space-16);
	height: var(--space-16);
	border: 0;
	border-radius: var(--border-radius-full);
	background: var(--neutral-200);
	color: var(--neutral-900);
	cursor: pointer;
	font: inherit;
	line-height: 1;

	&:hover:not(:disabled) {
		background: var(--neutral-400);
	}

	&:disabled {
		color: var(--neutral-600);
		cursor: not-allowed;
	}
}

.field-input {
	width: 100%;
	min-height: 40px;
	box-sizing: border-box;
	padding: var(--space-10);
	border: var(--border-width-thin) solid var(--neutral-400);
	border-radius: var(--border-radius-sm);
	background: var(--neutral-0);
	color: var(--neutral-900);
	font: inherit;

	&:focus {
		outline: none;
		border-color: var(--accent-info);
		box-shadow: 0 0 0 2px rgb(25 118 210 / 20%);
	}

	&:disabled {
		background: var(--neutral-50);
		color: var(--neutral-600);
		cursor: not-allowed;
	}
}

.permissions-trigger {
	width: 100%;
	min-height: 40px;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--space-10);
	padding: 0 var(--space-10);
	border: var(--border-width-thin) solid var(--neutral-400);
	border-radius: var(--border-radius-sm);
	background: var(--neutral-0);
	color: var(--neutral-700);
	font: inherit;
	cursor: pointer;

	&:hover:not(:disabled) {
		border-color: var(--neutral-600);
	}

	&:focus,
	&:focus-visible {
		outline: none;
		border-color: var(--accent-info);
		box-shadow: 0 0 0 2px rgb(25 118 210 / 20%);
	}

	&:disabled {
		background: var(--neutral-50);
		color: var(--neutral-600);
		cursor: not-allowed;
	}

	.chevron {
		flex: 0 0 auto;
		width: 6px;
		height: 6px;
		border-right: var(--border-width-thin) solid var(--neutral-600);
		border-bottom: var(--border-width-thin) solid var(--neutral-600);
		transform: rotate(45deg);
		margin-top: calc(var(--space-3) * -1);
		transition: transform 0.2s ease;

		&.open {
			transform: rotate(225deg);
			margin-top: var(--space-3);
		}
	}
}

.actions {
	display: flex;
	justify-content: flex-end;
	gap: var(--space-10);
}
</style>
